import type { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@trillion/ui/components/ui/badge";
import { createActionsColumn } from "@trillion/ui";
import { CheckCircle2, XCircle, MailPlus, MailCheck } from "lucide-react";
import { UserStatus } from "@trillion/enums";
import type { User } from "@/types";

// Keyed by the enum, so adding a member is a compile error until the label + colors are chosen.
const statusVariant: Record<UserStatus, { label: string; className: string }> = {
  [UserStatus.Active]: {
    label: "Active",
    className: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
  },
  [UserStatus.Invited]: {
    label: "Invited",
    className: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  },
  [UserStatus.Suspended]: {
    label: "Suspended",
    className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  },
  [UserStatus.Deleted]: {
    label: "Deleted",
    className: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  },
};

export function getUserColumns(
  onViewUser: (userId: string) => void,
  onResendInvite: (user: User) => void,
  onResendVerification: (user: User) => void
): ColumnDef<User>[] {
  return [
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => <span className="font-medium">{row.original.email}</span>,
    },
    {
      accessorKey: "display_name",
      header: "Display Name",
      cell: ({ row }) => (
        <span className="text-muted-foreground">{row.original.display_name || "-"}</span>
      ),
    },
    {
      accessorKey: "user_status_id",
      header: "Status",
      cell: ({ row }) => {
        const variant =
          statusVariant[row.original.user_status_id] ?? statusVariant[UserStatus.Active];
        return (
          <Badge variant="outline" className={variant.className}>
            {variant.label}
          </Badge>
        );
      },
    },
    {
      accessorKey: "email_verified",
      header: "Verified",
      cell: ({ row }) =>
        row.original.email_verified ? (
          <CheckCircle2 className="size-4 text-emerald-600" />
        ) : (
          <XCircle className="size-4 text-muted-foreground" />
        ),
    },
    {
      accessorKey: "created_date",
      header: "Created",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {new Date(row.original.created_date).toLocaleDateString()}
        </span>
      ),
    },
    createActionsColumn<User>({
      onView: (u) => onViewUser(u.user_id),
      // Both items are always PRESENT and greyed when they don't apply, so the row's capabilities read
      // the same everywhere — the enablement rules mirror the API's 409 gates exactly.
      items: [
        {
          label: "Resend invite",
          icon: MailPlus,
          onSelect: onResendInvite,
          disabled: (u) => u.user_status_id !== UserStatus.Invited,
        },
        {
          label: "Send verification email",
          icon: MailCheck,
          onSelect: onResendVerification,
          disabled: (u) => u.user_status_id !== UserStatus.Active || u.email_verified,
        },
      ],
    }),
  ];
}
